/**
 * AnalyticsOverview Component
 * Display analytics statistics with collapsible grid and premium styling
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader } from '../common';
import { StatsGrid } from './StatsGrid';
import { useAppDispatch, useAppSelector, jobsActions } from '../../redux';
import {
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Car,
  ArrowUpRight,
  ArrowDownRight,
  CircleDot,
  Utensils,
  BedDouble,
  CheckCircle2,
  AlertTriangle,
  PawPrint,
  Star,
} from 'lucide-react';

export const AnalyticsOverview: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stats, loading } = useAppSelector((state: any) => state.jobs);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    dispatch(jobsActions.fetchJobStats());
  }, [dispatch]);

  const additionalStats = stats ? [
    {
      show: stats.by_type?.number_plate_detection > 0,
      icon: <Car className="w-5 h-5" />,
      label: "Cars Detected",
      value: "0",
      color: "text-accent-400",
      bgColor: "from-accent-500/20 to-primary-500/20",
      category: "General"
    },
    {
      show: stats.by_type?.in_out_counting > 0,
      icon: <ArrowUpRight className="w-5 h-5" />,
      label: "In Count",
      value: "4",
      color: "text-emerald-400",
      bgColor: "from-emerald-500/20 to-primary-500/20",
      category: "General"
    },
    {
      show: stats.by_type?.in_out_counting > 0,
      icon: <ArrowDownRight className="w-5 h-5" />,
      label: "Out Count",
      value: "1",
      color: "text-red-400",
      bgColor: "from-red-500/20 to-orange-500/20",
      category: "General"
    },
    {
      show: stats.by_type?.pothole_detection > 0,
      icon: <CircleDot className="w-5 h-5" />,
      label: "Potholes Found",
      value: "3",
      color: "text-orange-400",
      bgColor: "from-orange-500/20 to-amber-500/20",
      category: "General"
    },
    {
      show: stats.by_type?.food_waste_estimation > 0,
      icon: <Utensils className="w-5 h-5" />,
      label: "Food Waste Calories",
      value: "60",
      color: "text-amber-400",
      bgColor: "from-amber-500/20 to-yellow-500/20",
      category: "General"
    },
    {
      show: stats.by_type?.room_readiness > 0,
      icon: <BedDouble className="w-5 h-5" />,
      label: "Avg Room Score",
      value: "55/100",
      color: "text-primary-400",
      bgColor: "from-primary-500/20 to-accent-500/20",
      category: "Hotel"
    },
    {
      show: stats.by_type?.room_readiness > 0,
      icon: <CheckCircle2 className="w-5 h-5" />,
      label: "Rooms Analyzed",
      value: "1",
      color: "text-emerald-400",
      bgColor: "from-emerald-500/20 to-teal-500/20",
      category: "Hotel"
    },
    {
      show: stats.by_type?.lobby_crowd_detection > 0,
      icon: <AlertTriangle className="w-5 h-5" />,
      label: "Lobby Alerts",
      value: "2",
      color: "text-red-400",
      bgColor: "from-red-500/20 to-rose-500/20",
      category: "Hotel"
    },
    {
      show: stats.by_type?.wildlife_detection > 0,
      icon: <PawPrint className="w-5 h-5" />,
      label: "Animals Detected",
      value: "1",
      color: "text-emerald-400",
      bgColor: "from-emerald-500/20 to-green-500/20",
      category: "Wildlife"
    },
    {
      show: stats.by_type?.wildlife_detection > 0,
      icon: <Star className="w-5 h-5" />,
      label: "Types of Animals",
      value: "1",
      color: "text-accent-400",
      bgColor: "from-accent-500/20 to-purple-500/20",
      category: "Wildlife"
    },
  ].filter(stat => stat.show) : [];

  return (
    <Card variant="glass" padding="lg">
      <CardHeader
        title="Analytics Overview"
        subtitle="Your key analysis statistics at a glance"
        icon={<TrendingUp className="w-6 h-6" />}
        action={
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        }
      />

      {isExpanded && (
        <div className="mt-6 space-y-6">
          <StatsGrid stats={stats} loading={loading} />

          {/* Additional Stats Cards */}
          {additionalStats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {additionalStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700/30 hover:border-slate-600/50 transition-all group"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br ${stat.bgColor} ${stat.color}`}>
                      {stat.icon}
                    </div>
                    <span className="text-sm text-slate-400 truncate">{stat.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <span className="text-xs text-slate-500">{stat.category}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};